/**
 * App Registry – Initializes core sockets and auth-related lifecycle hooks
 */

// Update the import path below to the correct location of authSlice, for example:
import { isDemoMode } from "../app/demoMode";
import type { AppDispatch } from "../app/store";
import {
  logout,
  refreshAccess,
  setUnauthorized,
} from "../features/auth/authSlice";
import { DemoSocketManager } from "./demoMode/DemoSocketManager";
import { ISocketManager } from "./ISocketManager";
import {
  scheduleSilentRefresh,
  shouldRetry,
  storedTokenNeedsRefresh,
} from "./sessionManagement/refreshToken";

import { socketEvents, SocketManager } from "./socketManager";

import { demoTasks } from "./demoMode/demoTasks";
import { TaskDto } from "../features/tasks/types";

// Enable this to trace all socket activity across namespaces
const LOG_SOCKET_ACTIVITY = true;

// Declare socket instances globally
let taskSocketManager: ISocketManager | null = null;
let calendarSocketManager: ISocketManager | null = null;

/**
 * Initializes global sockets and installs session refresh hooks
 */
const initializeApp = async (dispatch: AppDispatch) => {
  try {
    if (
      taskSocketManager?.isConnected() &&
      calendarSocketManager?.isConnected()
    ) {
      return;
    }

    const demo = isDemoMode();
    const SocketImpl = demo ? DemoSocketManager : SocketManager;

    if (!taskSocketManager) {
      taskSocketManager = new SocketImpl("tasks", {}, LOG_SOCKET_ACTIVITY);
      taskSocketManager.setEvents(
        createDefaultSocketEvents(dispatch, taskSocketManager),
      );
      if (demo && taskSocketManager instanceof DemoSocketManager) {
        taskSocketManager.registerMockResponse("createQuickTask", () => {
          const newTask: TaskDto = {
            id: crypto.randomUUID(),
            title: "Neuer Task",
            urgency: 0,
            type: "",
            importance: 0,
            scheduledStatus: "OPEN",
            delegatedStatus: "OPEN",
            kanbanStatus: "OPEN",
            storeStatus: "OPEN",
            activeStatus: "",
            updatedAt: new Date().toISOString(),
            NoteAccess: [],
          };
          demoTasks.push(newTask);
          return { event: "createQuickTaskAnswer", payload: newTask };
        });
      }
    }

    if (!calendarSocketManager) {
      calendarSocketManager = new SocketImpl(
        "calendar",
        {},
        LOG_SOCKET_ACTIVITY,
      );
      calendarSocketManager.setEvents(
        createDefaultSocketEvents(dispatch, calendarSocketManager),
      );
    }

    // Reopened after a break the token has usually expired. The gateway
    // accepts the connection anyway but rejects every message with it, so
    // refresh first instead of losing the first requests.
    if (!demo && storedTokenNeedsRefresh()) {
      try {
        await dispatch(refreshAccess()).unwrap();
      } catch {
        // Retried (or logged out) by scheduleSilentRefresh below
      }
    }

    if (LOG_SOCKET_ACTIVITY) console.log("🔌 Connecting sockets...");
    // In parallel: each connection is a round trip of its own
    await Promise.all([
      taskSocketManager.connect(),
      calendarSocketManager.connect(),
    ]);

    if (!demo) {
      scheduleSilentRefresh(
        [taskSocketManager, calendarSocketManager],
        true,
        dispatch,
      );
    } // Automatically refresh tokens in the background

    if (LOG_SOCKET_ACTIVITY)
      console.log("✅ Sockets initialized and session refresh configured.");
  } catch (error) {
    console.error("⚠️ Socket initialization failed. Disconnecting all.");
    taskSocketManager?.disconnect();
    calendarSocketManager?.disconnect();
  }
};

const SOCKET_REAUTH_RETRY_MS = 15_000;

/**
 * Gets a fresh token for a socket the server rejected. A temporary problem
 * (offline, 429, server restart) is retried instead of logging the user out.
 */
const reauthenticateSocket = async (
  dispatch: AppDispatch,
  socket: ISocketManager,
): Promise<void> => {
  try {
    await dispatch(refreshAccess()).unwrap();
    await socket.handleTokenRefresh();
  } catch (err) {
    if (shouldRetry(err)) {
      window.setTimeout(
        () => reauthenticateSocket(dispatch, socket),
        SOCKET_REAUTH_RETRY_MS,
      );
      return;
    }
    dispatch(setUnauthorized());
    socket.disconnect();
  }
};

/**
 * Creates socket event handlers with default auth-related logic
 * @param socket - The socket instance this handler set belongs to
 * @returns socketEvents object
 */
const createDefaultSocketEvents = (
  dispatch: AppDispatch,
  socket: ISocketManager,
): socketEvents => ({
  onUnauthenticated: () => reauthenticateSocket(dispatch, socket),
  onUnauthorized: async () => {
    await dispatch(setUnauthorized());
  },
  onceUnauthorized: async () => {
    window.location.replace("/");
  },
});

const logoutApp = async (dispatch: AppDispatch) => {
  await dispatch(logout());
  taskSocketManager?.disconnect();
  calendarSocketManager?.disconnect();

  // Important to guard against stale socket instances in logout-login cycles
  taskSocketManager = null;
  calendarSocketManager = null;
};

export { taskSocketManager, calendarSocketManager, initializeApp, logoutApp };
